import React from 'react';
import { X, Calendar, User } from 'lucide-react';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { Button } from '../../../components/common/Button';
import type { HolidayRequest } from '../../../utils/types';

interface UserHolidayDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userUsername: string;
  requests: HolidayRequest[];
  isLoading?: boolean;
  year: number;
  /** Authoritative taken days from all_stats (full DB aggregate). */
  takenDaysFromStats?: number;
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const UserHolidayDetailModal: React.FC<UserHolidayDetailModalProps> = ({
  isOpen,
  onClose,
  userName,
  userUsername,
  requests,
  isLoading,
  year,
  takenDaysFromStats,
}) => {
  if (!isOpen) return null;

  const approvedRequests = requests.filter((r) => r.status === 'approved');

  const totalDaysFromList = approvedRequests.reduce(
    (sum, r) => sum + Number(r.total_days || 0),
    0
  );
  const displayTotalDays =
    takenDaysFromStats !== undefined ? Number(takenDaysFromStats) : totalDaysFromList;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
          {/* Header */}
          <div className="bg-blue-50 px-4 py-3 sm:px-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-900">
                  {userName}
                </h3>
                <p className="text-sm text-blue-600">@{userUsername} • {year}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-blue-400 hover:text-blue-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Body */}
          <div className="px-4 py-5 sm:p-6">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : approvedRequests.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No approved holidays for {year}</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-700 font-medium">Total Approved Days</span>
                    <span className="text-2xl font-bold text-green-900">
                      {displayTotalDays.toFixed(2).replace(/\.00$/, '')} days
                    </span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    {approvedRequests.length} approved request{approvedRequests.length !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Reference
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          From
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          To
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Days
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {approvedRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className="text-xs font-mono text-gray-600">
                              {request.id.slice(0, 8).toUpperCase()}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(request.start_date)}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(request.end_date)}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {Number(request.total_days || 0).toFixed(2).replace(/\.00$/, '')} {request.is_half_day && '(Half)'}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-900 max-w-xs truncate" title={request.reason}>
                            {request.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          
          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserHolidayDetailModal;
